import React, { useState } from 'react';
import {
  Box, Button, IconButton, Select, MenuItem, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Typography, Chip,
  ToggleButton, ToggleButtonGroup, TextareaAutosize, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useEditMode } from './EditModeContext';
import axios from '../../auth/axiosConfig';

const SCORE_TYPE_LABELS = {
  TRIVIAL: 'Overall',
  KYB: 'KYB',
  FIRST_PARTY: 'First Party',
  SYNTHETIC: 'Synthetic',
};

export const RISK_SCORE_MAP = { LOW: 0.1, MODERATE: 0.55, HIGH: 0.9 };

export const RiskLevelEditor = ({ riskSummary, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  if (!isEditMode) return null;

  const handleRiskLevelChange = (scoreType, newLevel) => {
    const updated = riskSummary.map(s => {
      if (s.score_type !== scoreType) return s;
      const newScore = RISK_SCORE_MAP[newLevel] ?? s.score;
      return { ...s, risk_level: newLevel, score: newScore };
    });
    addChangeDescription(`Changed ${SCORE_TYPE_LABELS[scoreType]} risk to ${newLevel}`);
    onUpdate(updated);
  };

  return (
    <Box sx={{ mb: 2, p: 1.5, border: '1px dashed #4566EB', borderRadius: 1, backgroundColor: '#f8f9ff' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Edit Risk Levels</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {riskSummary.filter(s => s.score_type !== 'THIRD_PARTY').map(s => (
          <FormControl key={s.score_type} size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{SCORE_TYPE_LABELS[s.score_type]}</InputLabel>
            <Select
              value={s.risk_level}
              label={SCORE_TYPE_LABELS[s.score_type]}
              onChange={(e) => handleRiskLevelChange(s.score_type, e.target.value)}
            >
              <MenuItem value="LOW">LOW</MenuItem>
              <MenuItem value="MODERATE">MODERATE</MenuItem>
              <MenuItem value="HIGH">HIGH</MenuItem>
            </Select>
          </FormControl>
        ))}
      </Box>
    </Box>
  );
};

export const AssessmentEditor = ({ riskSummary, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [newAssessment, setNewAssessment] = useState({
    scoreType: 'TRIVIAL',
    listType: 'pos',
    ui_string: '',
  });

  if (!isEditMode) return null;

  const handleRemoveAssessment = (scoreType, listType, uiString) => {
    const listKey = listType === 'pos' ? 'top_pos_assessments' : 'top_neg_assessments';
    const updated = riskSummary.map(s => {
      if (s.score_type !== scoreType) return s;
      return {
        ...s,
        [listKey]: s[listKey].filter(a => a.ui_string !== uiString),
      };
    });
    addChangeDescription(`Removed "${uiString}" from ${SCORE_TYPE_LABELS[scoreType]}`);
    onUpdate(updated);
  };

  const handleAddAssessment = () => {
    if (!newAssessment.ui_string.trim()) return;

    // pos = top_pos_assessments = risk-increasing signals (score > 1.0, result_sign "Positive")
    // neg = top_neg_assessments = risk-decreasing mitigators (score < 1.0, result_sign "Negative")
    const listKey = newAssessment.listType === 'pos' ? 'top_pos_assessments' : 'top_neg_assessments';
    const isPos = newAssessment.listType === 'pos';

    const nameParts = newAssessment.ui_string.split(' - ');
    const assessmentName = nameParts[0]?.replace(/\s+/g, '') || 'ManualAssessment';

    const entry = {
      name: assessmentName,
      ui_string: newAssessment.ui_string,
      score: isPos ? 1.1 : 0.8,
      node_ids: [],
      result_sign: isPos ? 'Positive' : 'Negative',
      result_enum: `ResultEnum.MANUAL_${Date.now()}`,
    };

    const updated = riskSummary.map(s => {
      if (s.score_type !== newAssessment.scoreType) return s;
      return {
        ...s,
        [listKey]: [entry, ...s[listKey]],
      };
    });

    const label = isPos ? 'risk warning' : 'mitigator';
    addChangeDescription(`Added ${label} "${newAssessment.ui_string}" to ${SCORE_TYPE_LABELS[newAssessment.scoreType]}`);
    onUpdate(updated);
    setAddDialogOpen(false);
    setNewAssessment({ scoreType: 'TRIVIAL', listType: 'pos', ui_string: '' });
  };

  return (
    <>
      <Box sx={{ mb: 1, display: 'flex', gap: 1 }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => setAddDialogOpen(true)}
          sx={{ borderColor: '#4566EB', color: '#4566EB' }}
        >
          Add assessment
        </Button>
        <Button
          size="small"
          startIcon={<DeleteIcon />}
          variant="outlined"
          onClick={() => setManageDialogOpen(true)}
          sx={{ borderColor: '#d32f2f', color: '#d32f2f' }}
        >
          Remove assessments
        </Button>
      </Box>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add assessment</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={newAssessment.scoreType}
              label="Category"
              onChange={(e) => setNewAssessment(prev => ({ ...prev, scoreType: e.target.value }))}
            >
              {Object.entries(SCORE_TYPE_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={newAssessment.listType}
            exclusive
            onChange={(_, val) => val && setNewAssessment(prev => ({ ...prev, listType: val }))}
            size="small"
          >
            <ToggleButton value="pos" sx={{ color: '#d32f2f' }}>Risk warning</ToggleButton>
            <ToggleButton value="neg" sx={{ color: '#2e7d32' }}>Mitigator</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label="Assessment text"
            placeholder="e.g. Ownership analysis - anomalous ownership pattern detected"
            value={newAssessment.ui_string}
            onChange={(e) => setNewAssessment(prev => ({ ...prev, ui_string: e.target.value }))}
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddAssessment} disabled={!newAssessment.ui_string.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manageDialogOpen} onClose={() => setManageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Remove assessments</DialogTitle>
        <DialogContent>
          {riskSummary.filter(s => s.score_type !== 'THIRD_PARTY').map(section => {
            const posAssessments = section.top_pos_assessments || [];
            const negAssessments = section.top_neg_assessments || [];
            if (posAssessments.length === 0 && negAssessments.length === 0) return null;
            return (
              <Box key={section.score_type} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {SCORE_TYPE_LABELS[section.score_type]}
                </Typography>
                {posAssessments.map((a, i) => (
                  <Box key={`pos-${i}`} sx={{ display: 'flex', alignItems: 'center', py: 0.5, pl: 1, '&:hover': { backgroundColor: '#fff0f0' } }}>
                    <Chip label="Risk" size="small" sx={{ mr: 1, backgroundColor: '#ffebee', color: '#d32f2f', fontSize: 11 }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>{a.ui_string}</Typography>
                    <IconButton size="small" onClick={() => handleRemoveAssessment(section.score_type, 'pos', a.ui_string)} sx={{ color: '#d32f2f' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {negAssessments.map((a, i) => (
                  <Box key={`neg-${i}`} sx={{ display: 'flex', alignItems: 'center', py: 0.5, pl: 1, '&:hover': { backgroundColor: '#f0fff0' } }}>
                    <Chip label="Mitigator" size="small" sx={{ mr: 1, backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: 11 }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>{a.ui_string}</Typography>
                    <IconButton size="small" onClick={() => handleRemoveAssessment(section.score_type, 'neg', a.ui_string)} sx={{ color: '#d32f2f' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const AssessmentRemoveButton = ({ scoreType, listType, uiString, riskSummary, onUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  if (!isEditMode) return null;

  const handleRemove = (e) => {
    e.stopPropagation();
    const listKey = listType === 'pos' ? 'top_pos_assessments' : 'top_neg_assessments';
    const updated = riskSummary.map(s => {
      if (s.score_type !== scoreType) return s;
      return {
        ...s,
        [listKey]: s[listKey].filter(a => a.ui_string !== uiString),
      };
    });
    addChangeDescription(`Removed "${uiString}" from ${SCORE_TYPE_LABELS[scoreType]}`);
    onUpdate(updated);
  };

  return (
    <IconButton size="small" onClick={handleRemove} sx={{ ml: 0.5, color: '#d32f2f', p: 0.25 }}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

export const SummaryEditor = ({ summary, onUpdate }) => {
  const { isEditMode, addChangeDescription, verificationId, getDetailData } = useEditMode();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(summary || '');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState(null);

  if (!isEditMode) return null;

  const handleSave = () => {
    addChangeDescription('Updated human readable summary');
    onUpdate(editValue);
    setError(null);
    setIsEditing(false);
  };

  const openEditor = () => {
    setEditValue(summary || '');
    setError(null);
    setIsEditing(true);
  };

  const closeEditor = () => {
    setError(null);
    setIsEditing(false);
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    setError(null);
    try {
      const { data } = await axios.post(`/ui/${verificationId}/regenerate-summary`, {
        detail_data: getDetailData(),
      });
      setEditValue(data.summary);
      setIsEditing(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to regenerate summary');
    } finally {
      setIsRegenerating(false);
    }
  };

  const regenerateButton = (
    <Button
      size="small"
      variant="outlined"
      startIcon={isRegenerating ? <CircularProgress size={14} /> : <AutorenewIcon />}
      onClick={handleRegenerate}
      disabled={isRegenerating}
      sx={{ borderColor: '#4566EB', color: '#4566EB' }}
    >
      Regenerate
    </Button>
  );

  if (!isEditing) {
    return (
      <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<EditIcon />}
            variant="outlined"
            onClick={openEditor}
            disabled={isRegenerating}
            sx={{ borderColor: '#4566EB', color: '#4566EB' }}
          >
            Edit summary
          </Button>
          {regenerateButton}
        </Box>
        {error && <Typography variant="caption" color="error">{error}</Typography>}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2, p: 2, border: '1px dashed #4566EB', borderRadius: 1, backgroundColor: '#f8f9ff' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Edit summary</Typography>
      <TextareaAutosize
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        minRows={5}
        style={{ width: '100%', padding: 12, fontFamily: 'inherit', fontSize: 14, borderRadius: 4, border: '1px solid #ccc', boxSizing: 'border-box' }}
      />
      {error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{error}</Typography>}
      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" onClick={handleSave} disabled={isRegenerating} sx={{ backgroundColor: '#4566EB' }}>Apply</Button>
        {regenerateButton}
        <Button size="small" onClick={closeEditor} disabled={isRegenerating}>Cancel</Button>
      </Box>
    </Box>
  );
};
